/**
 * Theme color setting - admin_UI only, no backend/database involved
 * at all. A chosen theme is just three CSS variables
 * (--primary-color / --primary-color-dark / --primary-color-light)
 * set on the document root and saved to localStorage, which every
 * page's Add/Save buttons, focus rings, etc. already read via
 * var(--primary-color) (see the CSS files touched alongside this).
 *
 * This is deliberately per-browser, not per-deployment: each person
 * who logs into this admin_UI can pick their own theme without
 * affecting anyone else's, since nothing is written to the server.
 * If you want one fixed theme for every user of a given deployment
 * instead, set the :root defaults in src/index.css directly (that's
 * the actual "ship one branded build per client" lever) rather than
 * relying on this picker.
 */

const STORAGE_KEY = "themeColor";

// "Professional" here means: a currently-popular default (Ocean Blue,
// the app's original color) plus a handful of desaturated,
// business-appropriate options - no neon/pastel colors that would
// look out of place in an operations dashboard.
export const THEME_PRESETS = [

    { key: "ocean-blue", label: "Ocean Blue (default)", primary: "#2563eb", primaryDark: "#1d4ed8", primaryLight: "#dbeafe" },

    { key: "emerald-green", label: "Emerald Green", primary: "#059669", primaryDark: "#047857", primaryLight: "#d1fae5" },

    { key: "royal-purple", label: "Royal Purple", primary: "#7c3aed", primaryDark: "#6d28d9", primaryLight: "#ede9fe" },

    { key: "crimson-red", label: "Crimson Red", primary: "#dc2626", primaryDark: "#b91c1c", primaryLight: "#fee2e2" },

    { key: "slate-gray", label: "Slate Gray", primary: "#475569", primaryDark: "#334155", primaryLight: "#e2e8f0" },

    { key: "sunset-orange", label: "Sunset Orange", primary: "#ea580c", primaryDark: "#c2410c", primaryLight: "#ffedd5" },

    { key: "deep-teal", label: "Deep Teal", primary: "#0f766e", primaryDark: "#0c5c56", primaryLight: "#ccfbf1" },

];

const DEFAULT_THEME = THEME_PRESETS[0];

/**
 * Given any hex color, derives a reasonable "dark" hover shade and a
 * "light" tint - used only for the custom color picker, where there's
 * no preset triplet to fall back on.
 */
function deriveShades(hex) {

    const clean = hex.replace("#", "");

    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);

    const darken = (v) => Math.max(0, Math.round(v * 0.8));
    const lighten = (v) => Math.min(255, Math.round(v + (255 - v) * 0.85));

    const toHex = (v) => v.toString(16).padStart(2, "0");

    const dark = `#${toHex(darken(r))}${toHex(darken(g))}${toHex(darken(b))}`;
    const light = `#${toHex(lighten(r))}${toHex(lighten(g))}${toHex(lighten(b))}`;

    return { primary: hex, primaryDark: dark, primaryLight: light };

}

/**
 * Standard YIQ perceived-brightness formula - decides whether white
 * or dark text is more readable on a given background color. Used
 * for the Sidebar and Header, whose background now follows the theme
 * color directly, so a light theme color (pale yellow, light gray)
 * and a dark one (navy, maroon) both stay readable without needing a
 * separate manual "text color" choice in the picker.
 */
function getContrastTextColor(hex) {

    const clean = hex.replace("#", "");

    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);

    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    return brightness > 150 ? "dark" : "light";

}

/**
 * All the derived CSS variables for one theme color - primary
 * button/focus color (unchanged from before) plus everything the
 * Sidebar/Header need: background, main text, muted/secondary text,
 * hover/active overlays, and a border tint. Overlays are
 * semi-transparent black or white (rather than a fixed gray) so they
 * work regardless of which the base color's contrast came out light
 * or dark.
 */
function buildCssVariables(theme) {

    const mode = getContrastTextColor(theme.primary);

    const isLight = mode === "light";

    return {

        "--primary-color": theme.primary,

        "--primary-color-dark": theme.primaryDark,

        "--primary-color-light": theme.primaryLight,

        // The text color for anything using --primary-color as a
        // background (every "add-btn"/"save-btn"/"login-btn" style
        // primary button across the app, not just the sidebar/header).
        // Without this, a light custom color (e.g. #ebebeb) paired
        // with hardcoded white button text would be nearly unreadable.
        "--primary-text-color": isLight ? "#ffffff" : "#111827",

        "--sidebar-bg": theme.primary,

        "--sidebar-text": isLight ? "#ffffff" : "#111827",

        "--sidebar-text-muted": isLight ? "rgba(255,255,255,0.72)" : "rgba(17,24,39,0.65)",

        "--sidebar-hover-bg": isLight ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.08)",

        "--sidebar-active-bg": isLight ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.12)",

        "--sidebar-border": isLight ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.12)",

        "--sidebar-scrollbar": isLight ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.25)",

        "--header-bg": theme.primary,

        "--header-text": isLight ? "#ffffff" : "#111827"

    };

}

export function getSavedTheme() {

    try {

        const raw = localStorage.getItem(STORAGE_KEY);

        return raw ? JSON.parse(raw) : DEFAULT_THEME;

    } catch {

        return DEFAULT_THEME;

    }

}

/**
 * Applies a theme's colors as CSS variables on the document root.
 * Call this immediately on app startup (see src/index.js) so a
 * previously-saved theme is visible before first paint, not just
 * after the Theme Settings page happens to mount.
 */
export function applyTheme(theme) {

    const root = document.documentElement;

    const vars = buildCssVariables(theme);

    Object.entries(vars).forEach(([name, value]) => {

        root.style.setProperty(name, value);

    });

}

export function saveTheme(theme) {

    localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));

}

export function applyAndSaveTheme(theme) {

    applyTheme(theme);
    saveTheme(theme);

}

export function applyCustomColor(hex) {

    const theme = { key: "custom", label: "Custom", ...deriveShades(hex) };

    applyAndSaveTheme(theme);

    return theme;

}

export function resetToDefaultTheme() {

    applyAndSaveTheme(DEFAULT_THEME);

    return DEFAULT_THEME;

}

/** Called once at app startup - see src/index.js. */
export function initTheme() {

    applyTheme(getSavedTheme());

}
